import type { JSX } from "react";

/** Opening subtitle candidates — the caption-CTA batch from the July 2026 tone
 * experiment, uppercased for the landing type system, pushpin dropped. One per
 * rendered document (see `sublineIndex`). The first two, plus the six appended
 * at the end, are website-only — not in the caption rotation. The appended six
 * are the account's own sign-off lines from the Instagram captions, so the
 * landing and the feed ask in one voice. */
const SUBLINES = [
  "FOR THE EXPAT WHO LANDED LAST TUESDAY AND THE LOCAL WHO SWEARS THEY'VE SEEN IT ALL.",
  "FOR THE ONE STILL LEARNING THE STREET NAMES, AND THE ONE WHO NAMED THEM.",
  `SAVE THIS AND SEND IT TO THE FRIEND WHO ALWAYS ASKS "WHAT'S ON TONIGHT?"`,
  "SEND THIS TO THE GROUP CHAT BEFORE SOMEONE SUGGESTS JUST STAYING IN.",
  'SEND THIS TO THE GROUP CHAT BEFORE SOMEONE SAYS "WE SHOULD DO SOMETHING TONIGHT" AND NOBODY ANSWERS.',
  `SEND THIS TO THE GROUP CHAT BEFORE THE "I'M EASY, WHATEVER YOU LOT FANCY" MESSAGES START.`,
  'SEND THIS TO YOUR GROUP CHAT BEFORE SOMEONE SUGGESTS "JUST DRINKS AT OURS AGAIN".',
  "SEND THIS TO THE GROUP CHAT BEFORE SOMEONE SUGGESTS THE SAME BAR AGAIN.",
  `SAVE THIS AND SEND IT TO THE FRIEND WHOSE ANSWER IS ALWAYS "I DUNNO, YOU PICK."`,
  `SEND THIS TO YOUR FRIEND WHO CAN NEVER MAKE A DECISION, SO TONIGHT THEY DON'T HAVE TO.`,
  `SEND THIS TO THE FRIEND WHO'S ALWAYS "UP FOR ANYTHING" BUT SOMEHOW NEVER KNOWS WHERE ANYTHING IS.`,
  `SEND THIS TO THE FRIEND WHO'S ALWAYS THE LAST TO KNOW.`,
  `SEND THIS TO THE FRIEND WHO ALWAYS SAYS "I'M EASY" THEN HATES EVERY SUGGESTION.`,
  "SEND IT TO YOUR MOST INDECISIVE FRIEND AND LET THEM PICK.",
  "SAVE THIS AND SEND IT TO THE FRIEND WHOSE CAMERA ROLL IS ALREADY 90% SCREENSHOTS OF POSTS LIKE THIS.",
  'SAVE THIS FOR THE 6PM "SO WHAT ARE WE DOING?" PANIC.',
  'SAVE IT NOW, THANK YOURSELF AT 8PM WHEN THE "SO WHAT ARE WE DOING?" TEXTS START ROLLING IN.',
  `SAVE IT NOW, THANK YOURSELF LATER WHEN YOU'RE STANDING ON THE STREET WITH NO PLAN.`,
  `SAVE THIS FOR FUTURE YOU, THE ONE SWEARING THEY'LL BE MORE SPONTANEOUS THIS WEEK.`,
  `SAVE THIS FOR THE VERSION OF YOU THAT SWEARS THERE'S NEVER ANYTHING TO DO IN THIS CITY.`,
  "SAVE THIS NOW, FUTURE YOU AT 7PM WITH NO PLANS SAYS THANKS.",
  // Website-only: the account's own sign-off lines. Never add these to the captions.
  "TAG WHOEVER YOU'RE DRAGGING ALONG.",
  "SAVE IT FOR TOMORROW.",
  "WE'RE HERE. YOU COMING?",
  'SAVE THIS. "I FORGOT" ISN\'T A PLAN.',
  "PICK YOUR NIGHT. TAG YOUR PEOPLE.",
  "THE WEEK'S SORTED BEFORE YOU ASK.",
];

/**
 * Which subtitle this document prints. The pick is per rendered document, not
 * per mount: the home document is server-rendered and then hydrated, so a
 * `Math.random()` pick would make the two sides print different lines and React
 * would throw the server's markup away. The payload's `generatedAt` moves on the
 * hourly rebuild, which is the rotation the one-per-load rule always meant.
 * Without a seed (a browser rendering the directory with no payload) the first
 * line is the document's.
 */
function sublineIndex(seed: string | null | undefined): number {
  if (!seed) return 0;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(hash) % SUBLINES.length;
}

/**
 * Hero — the two opening scroll beats of the atlas scene. It owns no camera and
 * no scroll math: MapSection measures these beats inside `.atlas-scene` and
 * drives the single globe camera from their offsets. The headline is fixed and
 * the opening subtitle is chosen by the payload instant (see `sublineIndex`);
 * both are visible without an IntersectionObserver, so the beats read as plain
 * content the moment the ink paints, map or no map.
 */
export function Hero({ seed }: { seed?: string | null }): JSX.Element {
  const subline = SUBLINES[sublineIndex(seed)];

  return (
    <>
      <div className="atlas-masthead">
        <a className="atlas-wordmark" href="/">
          noko
        </a>
        <span className="atlas-tag">CITY EVENT GUIDE</span>
        <a className="atlas-masthead-link" href="#cities">
          FIND YOUR CITY
        </a>
      </div>

      <section className="atlas-beat atlas-beat-orbit" data-atlas-beat="orbit">
        <h1 className="atlas-display">
          <span className="atlas-display-line">EACH CITY.</span>
          <span className="atlas-display-line">TWO POSTS.</span>
          <span className="atlas-display-line">EVERY WEEK.</span>
        </h1>
        <p className="atlas-subline">{subline}</p>
        <a className="atlas-cta" href="#cities">
          <span className="atlas-cta-bar" aria-hidden="true" />
          <span className="atlas-cta-label">FIND YOUR CITY</span>
        </a>
        <p className="atlas-note">THE PICKS ARE ON INSTAGRAM.</p>
      </section>

      <section className="atlas-beat atlas-beat-approach" data-atlas-beat="approach">
        <p className="atlas-kicker">02 / CLOSER TO HOME</p>
        <h2 className="atlas-display">
          <span className="atlas-display-line">MIDWEEK.</span>
          <span className="atlas-display-line">WEEKEND.</span>
        </h2>
        <p className="atlas-subline">YOUR CITY'S PICKS, ON INSTAGRAM.</p>
      </section>
    </>
  );
}
